import React from 'react'
import { RegisteredPlayer, Team } from '@sportninja/common/types/Registration'
import { RegistrationTableDivision } from '../../types'

export type HighlightType = 'success' | 'error'

export interface HighlightEntry {
  type: HighlightType
  addedAt: number
}

export type ModalName =
  | 'divisionSetup'
  | 'divisionClone'
  | 'registrationDetails'
  | 'adminTeamRegister'
  | 'adminPlayerRegister'
  | 'bucket'
  | 'deleteBucket'
  | 'managePlayers'
  | 'manageTeams'
  | 'approveWithConditions'
  | 'cancelRegistration'
  | 'paidInPersonAlert'
  | 'billingSchedule'

export enum UIActionType {
  OPEN_MODAL = 'OPEN_MODAL',
  CLOSE_MODAL = 'CLOSE_MODAL',
  CLOSE_ALL_MODALS = 'CLOSE_ALL_MODALS',
  SET_SELECTED_DIVISION_MODAL = 'SET_SELECTED_DIVISION_MODAL',
  SET_SELECTED_TEAM = 'SET_SELECTED_TEAM',
  SET_SELECTED_PLAYER = 'SET_SELECTED_PLAYER',
  SET_BUCKET_TO_DELETE = 'SET_BUCKET_TO_DELETE',
  SET_SEARCH_TEXT = 'SET_SEARCH_TEXT',
  SET_SELECTED_STATUSES = 'SET_SELECTED_STATUSES',
  TOGGLE_TEAM_ROW = 'TOGGLE_TEAM_ROW',
  EXPAND_ALL_TEAM_ROWS = 'EXPAND_ALL_TEAM_ROWS',
  COLLAPSE_ALL_TEAM_ROWS = 'COLLAPSE_ALL_TEAM_ROWS',
  TOGGLE_DIVISION = 'TOGGLE_DIVISION',
  SET_OPEN_DIVISIONS = 'SET_OPEN_DIVISIONS',
  // Filter states
  SET_SELECTED_DIVISION = 'SET_SELECTED_DIVISION',
  SET_ONLY_BUCKETS = 'SET_ONLY_BUCKETS',
  SET_ONLY_DEFERRED_PAYMENTS = 'SET_ONLY_DEFERRED_PAYMENTS',
  SET_ALL_EXPANDED = 'SET_ALL_EXPANDED',
  // Player selection for trading
  SET_SELECTED_PLAYER_ROW_KEYS = 'SET_SELECTED_PLAYER_ROW_KEYS',
  SET_SELECTED_PLAYERS = 'SET_SELECTED_PLAYERS',
  // Team selection for moving
  SET_SELECTED_TEAM_ROW_KEYS = 'SET_SELECTED_TEAM_ROW_KEYS',
  SET_SELECTED_TEAMS_FOR_MOVE = 'SET_SELECTED_TEAMS_FOR_MOVE',
  SET_MOVE_TEAMS_ORIGIN_DIVISION = 'SET_MOVE_TEAMS_ORIGIN_DIVISION',
  CLEAR_TEAM_SELECTION = 'CLEAR_TEAM_SELECTION',
  // Player selection for trading
  CLEAR_PLAYER_SELECTION = 'CLEAR_PLAYER_SELECTION',
  // Registration details modal
  SET_REGISTRATION_DETAIL_DEFAULT_TAB = 'SET_REGISTRATION_DETAIL_DEFAULT_TAB',
  // Row highlighting
  ADD_HIGHLIGHTED_ROW = 'ADD_HIGHLIGHTED_ROW',
  REMOVE_HIGHLIGHTED_ROW = 'REMOVE_HIGHLIGHTED_ROW',
  CLEAR_EXPIRED_HIGHLIGHTS = 'CLEAR_EXPIRED_HIGHLIGHTS',
}

export interface UIState {
  modals: Record<ModalName, boolean>
  // Modal selections
  selectedDivisionModal: RegistrationTableDivision | null
  selectedTeam: Team | null
  selectedPlayer: RegisteredPlayer | null
  bucketToDelete: Team | null
  // Filter states
  searchText: string
  selectedStatuses: number[]
  selectedDivision: string // 'all' or division id for filter dropdown
  onlyBuckets: boolean
  onlyDeferredPayments: boolean
  allExpanded: boolean
  // Expansion states
  expandedTeamRows: Set<string>
  openDivisions: Record<string, boolean>
  // Player selection for trading
  selectedPlayerRowKeys: React.Key[]
  selectedPlayers: RegisteredPlayer[]
  // Team selection for moving
  selectedTeamRowKeys: React.Key[]
  selectedTeamsForMove: Team[]
  moveTeamsOriginDivisionId: string | null
  // Registration details modal
  registrationDetailDefaultTab: '0' | '1'
  // Row highlighting
  highlightedRows: Map<string, HighlightEntry>
}

export type UIAction =
  | { type: UIActionType.OPEN_MODAL; payload: ModalName }
  | { type: UIActionType.CLOSE_MODAL; payload: ModalName }
  | { type: UIActionType.CLOSE_ALL_MODALS }
  | {
      type: UIActionType.SET_SELECTED_DIVISION_MODAL
      payload: RegistrationTableDivision | null
    }
  | { type: UIActionType.SET_SELECTED_TEAM; payload: Team | null }
  | { type: UIActionType.SET_SELECTED_PLAYER; payload: RegisteredPlayer | null }
  | { type: UIActionType.SET_BUCKET_TO_DELETE; payload: Team | null }
  | { type: UIActionType.SET_SEARCH_TEXT; payload: string }
  | { type: UIActionType.SET_SELECTED_STATUSES; payload: number[] }
  | { type: UIActionType.TOGGLE_TEAM_ROW; payload: string }
  | { type: UIActionType.EXPAND_ALL_TEAM_ROWS; payload: string[] }
  | { type: UIActionType.COLLAPSE_ALL_TEAM_ROWS }
  | { type: UIActionType.TOGGLE_DIVISION; payload: string }
  | { type: UIActionType.SET_OPEN_DIVISIONS; payload: Record<string, boolean> }
  // Filter actions
  | { type: UIActionType.SET_SELECTED_DIVISION; payload: string }
  | { type: UIActionType.SET_ONLY_BUCKETS; payload: boolean }
  | { type: UIActionType.SET_ONLY_DEFERRED_PAYMENTS; payload: boolean }
  | { type: UIActionType.SET_ALL_EXPANDED; payload: boolean }
  // Player selection actions
  | { type: UIActionType.SET_SELECTED_PLAYER_ROW_KEYS; payload: React.Key[] }
  | { type: UIActionType.SET_SELECTED_PLAYERS; payload: RegisteredPlayer[] }
  // Team selection for moving
  | { type: UIActionType.SET_SELECTED_TEAM_ROW_KEYS; payload: React.Key[] }
  | { type: UIActionType.SET_SELECTED_TEAMS_FOR_MOVE; payload: Team[] }
  | {
      type: UIActionType.SET_MOVE_TEAMS_ORIGIN_DIVISION
      payload: string | null
    }
  | { type: UIActionType.CLEAR_TEAM_SELECTION }
  // Player selection for trading
  | { type: UIActionType.CLEAR_PLAYER_SELECTION }
  // Registration details modal
  | {
      type: UIActionType.SET_REGISTRATION_DETAIL_DEFAULT_TAB
      payload: '0' | '1'
    }
  // Row highlighting
  | {
      type: UIActionType.ADD_HIGHLIGHTED_ROW
      payload: { id: string; highlightType: HighlightType; addedAt: number }
    }
  | { type: UIActionType.REMOVE_HIGHLIGHTED_ROW; payload: string }
  | { type: UIActionType.CLEAR_EXPIRED_HIGHLIGHTS; payload: number }

export interface UIContextValue extends UIState {
  openModal: (modal: ModalName) => void
  closeModal: (modal: ModalName) => void
  closeAllModals: () => void
  setSelectedDivisionModal: (division: RegistrationTableDivision | null) => void
  setSelectedTeam: (team: Team | null) => void
  setSelectedPlayer: (player: RegisteredPlayer | null) => void
  setBucketToDelete: (bucket: Team | null) => void
  setSearchText: (text: string) => void
  setSelectedStatuses: (statuses: number[]) => void
  toggleTeamRow: (teamId: string) => void
  expandAllTeamRows: (teamIds: string[]) => void
  collapseAllTeamRows: () => void
  toggleDivision: (divisionId: string) => void
  setOpenDivisions: (divisions: Record<string, boolean>) => void
  isModalOpen: (modal: ModalName) => boolean
  isTeamRowExpanded: (teamId: string) => boolean
  isDivisionOpen: (divisionId: string) => boolean
  // Filter actions
  setSelectedDivision: (divisionId: string) => void
  setOnlyBuckets: (value: boolean) => void
  setOnlyDeferredPayments: (value: boolean) => void
  setAllExpanded: (value: boolean) => void
  // Player selection actions
  setSelectedPlayerRowKeys: (keys: React.Key[]) => void
  setSelectedPlayers: (players: RegisteredPlayer[]) => void
  clearPlayerSelection: () => void
  // Team selection for moving
  setSelectedTeamRowKeys: (keys: React.Key[]) => void
  setSelectedTeamsForMove: (teams: Team[]) => void
  setMoveTeamsOriginDivision: (divisionId: string | null) => void
  clearTeamSelection: () => void
  // Registration details modal
  setRegistrationDetailDefaultTab: (tab: '0' | '1') => void
  // Row highlighting
  addHighlightedRow: (id: string, highlightType?: HighlightType) => void
}
